import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import "@/App.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/layout/DashboardLayout";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuditLogs from "@/pages/AuditLogs";
import PublicReceipt from "@/pages/PublicReceipt";

import SuperAdminDashboard from "@/pages/superadmin/Dashboard";
import SchoolsList from "@/pages/superadmin/SchoolsList";
import SchoolDetail from "@/pages/superadmin/SchoolDetail";

import SchoolAdminDashboard from "@/pages/schooladmin/Dashboard";
import BrandingSettings from "@/pages/schooladmin/BrandingSettings";
import Sessions from "@/pages/schooladmin/Sessions";
import Teachers from "@/pages/schooladmin/Teachers";
import Cashiers from "@/pages/schooladmin/Cashiers";
import Classes from "@/pages/schooladmin/Classes";
import Students from "@/pages/schooladmin/Students";
import FeeStructure from "@/pages/schooladmin/FeeStructure";
import Payments from "@/pages/schooladmin/Payments";
import Reports from "@/pages/schooladmin/Reports";
import Defaulters from "@/pages/schooladmin/Defaulters";
import ConcessionsReport from "@/pages/schooladmin/ConcessionsReport";
import MonthlyVerification from "@/pages/schooladmin/MonthlyVerification";
import CollectionReconReport from "@/pages/schooladmin/CollectionReconReport";

import TeacherDashboard from "@/pages/teacher/Dashboard";
import TeacherCollect from "@/pages/teacher/Collect";
import OperationsDashboard from "@/pages/operations/OperationsDashboard";
import { SpecialCollectionsRoutes } from "@/pages/specialcollections/SpecialCollections";

function RoleHomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <SuperAdminDashboard />;
  if (user?.role === "school_admin") return <SchoolAdminDashboard />;
  if (user?.role === "teacher") return <TeacherDashboard />;
  if (user?.role === "cashier") return <OperationsDashboard />;
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Public branded receipt landing — no auth (slug is the capability) */}
          <Route path="/r/:slug" element={<PublicReceipt />} />

          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<RoleHomeRedirect />} />

            {/* Super admin routes */}
            <Route
              path="/schools"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <SchoolsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schools/:id"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <SchoolDetail />
                </ProtectedRoute>
              }
            />

            {/* School admin routes */}
            <Route
              path="/branding"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <BrandingSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sessions"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <Sessions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teachers"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <Teachers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cashiers"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <Cashiers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/classes"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <Classes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/students"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <Students />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fees-structure"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <FeeStructure />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute roles={["school_admin", "cashier"]}>
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/receipts"
              element={
                <ProtectedRoute roles={["school_admin", "teacher", "cashier"]}>
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/defaulters"
              element={
                <ProtectedRoute roles={["school_admin", "cashier"]}>
                  <Defaulters />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/concessions"
              element={
                <ProtectedRoute roles={["school_admin"]}>
                  <ConcessionsReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/monthly-verification"
              element={
                <ProtectedRoute roles={["school_admin", "cashier", "teacher"]}>
                  <MonthlyVerification />
                </ProtectedRoute>
              }
            />
            {/* Phase 30L — Collection & Reconciliation Report.
                Accessible to school_admin AND cashier (both perform daily
                accounts closing). Server-side feature-flag gate (404). */}
            <Route
              path="/reports/collection-recon"
              element={
                <ProtectedRoute roles={["school_admin", "cashier"]}>
                  <CollectionReconReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/collect"
              element={
                <ProtectedRoute roles={["teacher", "cashier"]}>
                  <TeacherCollect />
                </ProtectedRoute>
              }
            />

            {/* Phase X1 — Special Collections (admin + cashier, feature-flag gated server-side) */}
            <Route
              path="/special-collections/*"
              element={
                <ProtectedRoute roles={["school_admin", "cashier", "super_admin"]}>
                  <SpecialCollectionsRoutes />
                </ProtectedRoute>
              }
            />

            {/* Audit (super admin OR school admin — different scopes) */}
            <Route
              path="/audit"
              element={
                <ProtectedRoute roles={["super_admin", "school_admin"]}>
                  <AuditLogsByRole />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function AuditLogsByRole() {
  const { user } = useAuth();
  return <AuditLogs scope={user?.role === "super_admin" ? "super" : "school"} />;
}

export default App;
